import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
} from "lucide-react";
import { authFetch } from "../lib/api";

const MONTH = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const getMonday = (d = new Date()) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

const toISO = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

const fmtShort = (d) => {
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")} ${MONTH[dt.getMonth()]}`;
};

const fmtDay = (d) => DAY[new Date(d).getDay()];

const weekLabel = (monday) => {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return `${fmtShort(monday)} - ${fmtShort(sunday)} , ${monday.getFullYear()}`;
};

const TaskTimesheetPanel = ({
  projectId,
  taskId,
  taskName,
  projectName,
  onClose,
}) => {
  const [monday, setMonday] = useState(getMonday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showWeekPicker, setShowWeekPicker] = useState(false);

  // List of weeks that have submitted timesheets for this task
  const [submittedWeeks, setSubmittedWeeks] = useState([]);
  const [weeksLoading, setWeeksLoading] = useState(true);

  // Fetch all submitted weeks for this task once
  useEffect(() => {
    const fetchWeeks = async () => {
      setWeeksLoading(true);
      try {
        // Fetch timesheets for this project/task across recent months
        // We go back 12 weeks to find submitted weeks
        const weeks = [];
        const now = new Date();
        for (let i = 0; i < 12; i++) {
          const mon = getMonday(
            new Date(now.getFullYear(), now.getMonth(), now.getDate() - i * 7),
          );
          const sun = new Date(mon);
          sun.setDate(mon.getDate() + 6);
          const res = await authFetch(
            `/api/reports/projects/${projectId}/tasks/${taskId}/timesheet?startDate=${toISO(mon)}&endDate=${toISO(sun)}`,
          );
          const json = await res.json();
          // Only include weeks where at least one resource has submitted
          if (
            json?.data?.resources?.some(
              (r) => r.submissionStatus === "Submitted" || r.totalHours > 0,
            )
          ) {
            weeks.push(new Date(mon));
          }
        }
        setSubmittedWeeks(weeks);
        // Default to the most recent submitted week
        if (weeks.length > 0) setMonday(weeks[0]);
      } catch {
      } finally {
        setWeeksLoading(false);
      }
    };
    fetchWeeks();
  }, [projectId, taskId]);

  // Fetch timesheet data for selected week
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const sun = new Date(monday);
      sun.setDate(monday.getDate() + 6);
      const res = await authFetch(
        `/api/reports/projects/${projectId}/tasks/${taskId}/timesheet?startDate=${toISO(monday)}&endDate=${toISO(sun)}`,
      );
      const json = await res.json();
      if (json?.data) setData(json.data);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [projectId, taskId, monday]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const goWeek = (dir) => {
    setMonday((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + dir * 7);
      return d;
    });
  };

  const resources = data?.resources || [];
  const status = data?.status || "On Track";
  const statusColor =
    status === "Completed"
      ? "#22c55e"
      : status === "At Risk"
        ? "#ef4444"
        : "#3b82f6";

  // Download as Excel
  const handleDownload = () => {
    const sun = new Date(monday);
    sun.setDate(monday.getDate() + 6);
    const header = [
      "Member",
      "Role",
      "Total Hours",
      "Submission Status",
      ...(resources[0]?.dailyLogs?.map((l) => fmtShort(l.date)) || []),
    ].join("\t");
    const rows = resources.map((r) =>
      [
        r.name,
        r.role,
        r.totalHours,
        r.submissionStatus,
        ...(r.dailyLogs || []).map((l) => l.display),
      ].join("\t"),
    );
    const content = [header, ...rows].join("\n");
    const blob = new Blob([content], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `timesheet_${taskName}_${toISO(monday)}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="reports-task-details-card"
      style={{
        boxSizing: "border-box",
        width: "100%",
        background: "#fff",
        borderRadius: 16,
        padding: 24,
        boxShadow: "0 0 4px rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flexShrink: 0,
          paddingBottom: 18,
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <h3
            style={{
              margin: 0,
            fontSize: 20,
            fontWeight: 500,
            color: "#111",
            }}
          >
            Task Details
          </h3>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "rgba(0,0,0,0.5)",
              padding: 0,
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Task name + status */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 4,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: 500,
              color: "#111",
            }}
          >
            {taskName}
          </p>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 13,
              color: "rgba(0,0,0,0.6)",
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: statusColor,
                display: "inline-block",
              }}
            />
            {status}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <span style={{ fontSize: 14, color: "#7F7F7F" }}>Project :</span>
          <span style={{ fontSize: 14, color: "#111", fontWeight: 400 }}>
            {projectName}
          </span>
        </div>

        <div style={{ borderTop: "1px solid #f1f5f9", marginBottom: 16 }} />

        {/* Week selector */}
        <div style={{ position: "relative", marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: "8px 12px",
              cursor: "pointer",
              background: "#fafafa",
            }}
            onClick={() => setShowWeekPicker((v) => !v)}
          >
            <Calendar size={13} color="#6366f1" />
            <span style={{ fontSize: 12, color: "#374151", flex: 1 }}>
              {weeksLoading ? "Loading weeks..." : weekLabel(monday)}
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goWeek(-1);
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "#9ca3af",
                  padding: "0 2px",
                  display: "flex",
                }}
              >
                <ChevronLeft size={13} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goWeek(1);
                }}
                disabled={monday >= getMonday()}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: monday >= getMonday() ? "#d1d5db" : "#9ca3af",
                  padding: "0 2px",
                  display: "flex",
                }}
              >
                <ChevronRight size={13} />
              </button>
            </div>
            <ChevronDown size={13} color="#9ca3af" />
          </div>

          {/* Week dropdown — shows submitted weeks, or a clear empty state
              instead of opening to nothing when there are none yet. */}
          {showWeekPicker && !weeksLoading && (
            <>
              <div
                style={{ position: "fixed", inset: 0, zIndex: 10 }}
                onClick={() => setShowWeekPicker(false)}
              />
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  left: 0,
                  right: 0,
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                  zIndex: 20,
                  overflow: "hidden",
                  maxHeight: 200,
                  overflowY: "auto",
                }}
              >
                {submittedWeeks.length === 0 ? (
                  <div
                    style={{
                      padding: "14px",
                      fontSize: 12,
                      color: "#9ca3af",
                      textAlign: "center",
                    }}
                  >
                    No submitted weeks found for this task in the last 12
                    weeks.
                  </div>
                ) : (
                  submittedWeeks.map((wk, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setMonday(wk);
                        setShowWeekPicker(false);
                      }}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "10px 14px",
                        border: "none",
                        fontSize: 12,
                        background:
                          toISO(wk) === toISO(monday) ? "#eef2ff" : "#fff",
                        color:
                          toISO(wk) === toISO(monday) ? "#4338ca" : "#374151",
                        cursor: "pointer",
                      }}
                    >
                      {weekLabel(wk)}
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>

      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          paddingTop: 18,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Timesheet heading */}
        <p
          style={{
            margin: "0 0 2px",
            fontSize: 14,
            fontWeight: 700,
            color: "#111827",
          }}
        >
          Timesheet
        </p>
        <p style={{ margin: "0 0 14px", fontSize: 11, color: "#9ca3af" }}>
          Daily hours entered by each assigned resource
        </p>

        {/* Resource cards */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: 4 }}>
        {loading ? (
          <p
            style={{
              fontSize: 12,
              color: "#9ca3af",
              textAlign: "center",
              padding: "20px 0",
            }}
          >
            Loading...
          </p>
        ) : resources.length === 0 ? (
          <p
            style={{
              fontSize: 12,
              color: "#9ca3af",
              textAlign: "center",
              padding: "20px 0",
            }}
          >
            No timesheet data for this week
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {resources.map((member, index) => {
              const badgeBg =
                member.submissionStatus === "Submitted" ? "#dcfce7" : "#fff3e0";
              const badgeColor =
                member.submissionStatus === "Submitted" ? "#16a34a" : "#ea580c";
              const badgeLabel =
                member.submissionStatus === "Submitted"
                  ? "Submitted"
                  : "Auto - Submitted";
              const avatarBg = ["#bfdbfe", "#ddd6fe", "#bbf7d0", "#fde68a"][
                index % 4
              ];

              return (
                <div
                  key={member._id || index}
                  style={{
                    border: "1px solid #f1f5f9",
                    borderRadius: 14,
                    padding: "12px 12px 10px",
                    background: "#fafbff",
                  }}
                >
                  {/* Member header row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: avatarBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#374151",
                        flexShrink: 0,
                        overflow: "hidden",
                      }}
                    >
                      {member.profilePicture ? (
                        <img
                          src={member.profilePicture}
                          alt={member.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        (member.name || "?")[0].toUpperCase()
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#111827",
                        }}
                      >
                        {member.name}
                      </p>
                      <p
                        style={{
                          margin: "1px 0 0",
                          fontSize: 10,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        {member.role}
                      </p>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 4,
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#374151",
                        }}
                      >
                        {member.totalHoursDisplay ||
                          `${member.totalHours || 0}h`}
                      </span>
                      <span
                        style={{
                          background: badgeBg,
                          color: badgeColor,
                          fontSize: 10,
                          fontWeight: 700,
                          padding: "3px 8px",
                          borderRadius: 999,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {badgeLabel}
                      </span>
                    </div>
                  </div>

                  {/* Daily logs grid */}
                  {(member.dailyLogs || []).length > 0 && (
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          minWidth: 260,
                        }}
                      >
                        <thead>
                          <tr>
                            {member.dailyLogs.map((log, di) => (
                              <td
                                key={di}
                                style={{
                                  textAlign: "center",
                                  paddingBottom: 4,
                                  minWidth: 34,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 9,
                                    color: "#9ca3af",
                                    fontWeight: 500,
                                  }}
                                >
                                  {fmtShort(log.date)}
                                </div>
                                <div style={{ fontSize: 9, color: "#c4cad4" }}>
                                  {fmtDay(log.date)}
                                </div>
                              </td>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {member.dailyLogs.map((log, di) => (
                              <td
                                key={di}
                                style={{
                                  textAlign: "center",
                                  padding: "4px 2px",
                                }}
                              >
                                <span
                                  style={{
                                    display: "inline-block",
                                    fontSize: 11,
                                    fontWeight: log.isLeave ? 600 : 500,
                                    color: log.isLeave
                                      ? "#f59e0b"
                                      : log.display === "-" ||
                                          log.display === "0"
                                        ? "#d1d5db"
                                        : "#374151",
                                    background: log.isLeave
                                      ? "#fef9ec"
                                      : "transparent",
                                    borderRadius: 4,
                                    padding: log.isLeave ? "1px 4px" : 0,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {log.display === "0" || log.display === "0.00"
                                    ? "-"
                                    : log.display}
                                </span>
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </div>

        {/* Footer buttons */}
        <div
          style={{
            marginTop: 18,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            flexShrink: 0,
          }}
        >
        <button
          onClick={handleDownload}
          style={{
            height: 46,
            border: "none",
            borderRadius: 12,
            background: "#4253C7",
            color: "#fff",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <Download size={18} />
          Download
        </button>
        <button
          onClick={onClose}
          style={{
            height: 46,
            border: "1.5px solid #d1d5db",
            borderRadius: 12,
            background: "#fff",
            color: "#6b7280",
            fontSize: 15,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        </div>
      </div>
    </div>
  );
};

export default TaskTimesheetPanel;
